import React, { useContext, useEffect, useState } from "react";
import { FormElement, FormLabel, FormInput, FormSelector } from "../StyledElements";
import { Dialog } from "../Dialog";
import { AppContext } from "../../contexts/AppContext";
import Swal from "sweetalert2";
import { getUser, saveUser } from "../../services/users";

export const EditUserDialog = ({ user_id, show, onClose }) => {
  const { clients, userTypes, selectedClient, hasPlatformFeature } = useContext(AppContext);
  const [localUser, setLocalUser] = useState({
    public_id: "",
    firstname: "",
    lastname: "",
    email: "",
    password: null,

  });

  const [localSelectedClient, setLocalSelectedClient] = useState(selectedClient);
  const [clientOptions, setClientOptions] = useState([]);
  const [userTypeOptions, setUserTypeOptions] = useState([]);
  const [saveText, setSaveText] = useState("Save");

  const isAdmin = window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (!show) {
      setLocalUser({
        public_id: "",
        firstname: "",
        lastname: "",
        email: "",
        password: null,
      });
      setLocalSelectedClient(selectedClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useEffect(() => {
    if (user_id) {
      // Here you would typically fetch the user details using the user_id and set it to localUser
      // For demonstration, we'll just set some dummy data
      const fetchUser = async () => {
        console.log("Fetching user details for user_id: ", user_id);
        const results = await getUser(user_id, true);
        if (results.success) {
          const userData = results.user;
          console.log("!!! USER DATA: ", userData);
          setLocalUser({
            public_id: userData.public_id,
            firstname: userData.firstname,
            lastname: userData.lastname,
            email: userData.email,
            password: null, // Password is typically not returned for security reasons
            client_id: userData.client_id,
            user_type: userData.user_type,
            comped_subscription: userData.comped_subscription,
            email_notifications_enabled: userData.email_notifications_enabled,
            user_group: userData.user_group,
            client_field_values: userData.client_field_values || {},
          });
          const client = clients.find(c => c.id === userData.client_id);
          setLocalSelectedClient(client);
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to fetch user details",
            icon: "error",
            button: "OK",
            confirmButtonText: "OK",
          });
          onClose();
        }
      };
      fetchUser();
    } else {
      setLocalUser({
        id: "",
        firstname: "",
        lastname: "",
        email: "",
        password: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user_id]);

  useEffect(() => {
    if (userTypes) {
      console.log("!!! USER TYPES: ", userTypes);
      const options = userTypes.filter((t) => {
        return isAdmin || (!isAdmin && t.is_client_type);
      }).map((type) => ({
        value: type.id,
        label: type.name.charAt(0).toUpperCase() + type.name.slice(1),
      }));
      setUserTypeOptions(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTypes]);

  useEffect(() => {
    if (clients) {
      console.log("!!! CLIENTS: ", clients);
      const options = clients.sort((a, b) => a.name > b.name ? 1 : -1).map((client) => ({
        value: client.id,
        label: client.name,
      }));
      setClientOptions(options);
    }
  }, [clients]);

  const saveLocalUser = async () => {
    setSaveText("Saving...");
    console.log("Saving user: ", localUser);
    // Here you would typically dispatch an action or call a service to save the user
    const client = (selectedClient && !isAdmin) ? selectedClient : clients.find(c => c.id === localUser.client_id);
    console.log("Selected client for user: ", client);

    //localUser.client = localSelectedClient;
    //localUser.client.public_id = localSelectedClient?.id;
    //localUser.client_id = localSelectedClient?.id;

    localUser.client = { ...client };
    localUser.client.public_id = localUser.client.id;
    localUser.client_id = localUser.client.id;

    console.log("!!! FINAL USER OBJECT TO SAVE: ", localUser);
    const results = await saveUser(localUser, true);
    setSaveText("Save");
    if (results.success) {
      Swal.fire({
        title: "Success",
        text: "User saved successfully",
        icon: "success",
        button: "OK",
        confirmButtonText: "OK",
      });
      onClose(results.user);
    } else {
      Swal.fire({
        title: "Error",
        text: results.message,
        icon: "error",
        button: "OK",
        confirmButtonText: "OK",
      });
    }
  }

  return (
    <Dialog
      title={localUser?.id ? "Edit User" : "Create User"}
      show={show}
      onClose={() => onClose()}
      onOK={saveLocalUser}
      okText={saveText}
      closeText="Close"
      style={{ maxWidth: "800px" }}
    >
      <FormElement style={{ marginBottom: "10px" }}>
        <FormLabel label="ID" />
        <FormInput
          value={localUser?.public_id ?? ""}
          style={{ backgroundColor: "#f0f0f0" }}
          disabled={true} // Disable ID field if editing an existing user
        />
      </FormElement>

      {
        isAdmin ?
          (<FormElement>
            <FormLabel label="Client" />
            <FormSelector
              value={localUser.client_id ?? ""}
              options={[
                { value: "", label: "Select Client" },
                ...clientOptions,
              ]}
              onChange={(e) => {
                setLocalSelectedClient(clients.find(c => c.id === e.target.value));
                setLocalUser({ ...localUser, client_id: e.target.value });
              }}
            />
          </FormElement>) : null}

      <FormElement>
        <FormLabel label="First Name" />
        <FormInput
          value={localUser?.firstname ?? ""}
          placeholder="Enter first name"
          onChange={(e) => {
            setLocalUser({ ...localUser, firstname: e.target.value });
          }}
        />
      </FormElement>

      <FormElement>
        <FormLabel label="Last Name" />
        <FormInput
          value={localUser?.lastname ?? ""}
          placeholder="Enter last name"
          onChange={(e) => {
            setLocalUser({ ...localUser, lastname: e.target.value });
          }}
        />
      </FormElement>

      <FormElement>
        <FormLabel label="Email" />
        <FormInput
          value={localUser?.email ?? ""}
          placeholder="Enter email"
          onChange={(e) => {
            setLocalUser({ ...localUser, email: e.target.value });
          }}
        />
      </FormElement>

      <FormElement>
        <FormLabel label="User Type" />
        <FormSelector
          value={localUser.user_type ? userTypeOptions.find(ut => ut.value === localUser.user_type)?.value : ""}
          options={[
            { value: "", label: "Choose User Type" },
            ...userTypeOptions,
          ]}
          onChange={(e) => {
            console.log("Selected user type value: ", e.target.value);
            setLocalUser({ ...localUser, user_type: e.target.value });
          }}
        />
      </FormElement>


      <FormElement>
        <FormLabel label="Password" />
        <FormInput
          type="password"
          placeholder="Enter password"
          autoComplete="new-password"
          value={localUser?.password ?? ""}
          onChange={(e) => {
            setLocalUser({ ...localUser, password: e.target.value });
          }}
        />
      </FormElement>
      {
        hasPlatformFeature("groups") && (
          <FormElement>
            <FormLabel label="Group" />
            <FormInput
              placeholder="Enter group"
              value={localUser?.user_group ?? ""}
              autoComplete="off"
              onChange={(e) => {
                setLocalUser({ ...localUser, user_group: e.target.value });
              }}
            />
          </FormElement>)
      }
      {
        hasPlatformFeature("subscriptions") && (
          <FormElement>
            <FormLabel label="Comped Subscription" />
            <FormSelector
              value={localUser.comped_subscription ? "true" : "false"}
              options={[
                { value: "true", label: "True" },
                { value: "false", label: "False" },
              ]}
              onChange={(e) => {
                setLocalUser({ ...localUser, comped_subscription: e.target.value === "true" });
              }}
            />
          </FormElement>
        )
      }
      {
        hasPlatformFeature("email notifications") && (
          <FormElement>
            <FormLabel label="Email Notifications" />
            <FormSelector
              value={localUser.email_notifications_enabled ? "true" : "false"}
              options={[
                { value: "true", label: "True" },
                { value: "false", label: "False" },
              ]}
              onChange={(e) => {
                setLocalUser({ ...localUser, email_notifications_enabled: e.target.value === "true" });
              }}
            />
          </FormElement>)
      }
      {
        localSelectedClient?.user_fields?.map((field) => (
          <FormElement key={field.public_id}>
            <FormLabel label={field.title} />
            <FormInput
              label={field.title}
              type={field.type}
              placeholder={field.title}
              name={field.public_id}
              value={localUser?.client_field_values?.[field.public_id] ?? ""}
              onChange={(e) => {
                let value = e.target.value;
                if (field.type === "phone_number") {
                  // make the format (123) 456-7890 while typing
                  const cleaned = value.replace(/\D/g, "");
                  // // format the cleaned number
                  if (cleaned.length > 3 && cleaned.length <= 6) {
                    value = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
                  } else if (cleaned.length > 6) {
                    value = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
                  }
                }
                const u = { ...localUser };
                if (!u.client_field_values) {
                  u.client_field_values = {};
                }

                u.client_field_values[field.public_id] = value;
                setLocalUser(u);
              }}
              autoComplete="off"
            />
          </FormElement>
        ))
      }
    </Dialog>
  );
};

export default EditUserDialog;
